import {useCallback, useEffect, useRef} from 'react';
import {
  TrackEventUBA,
  TrackUbaAudit,
  getUbaPageName,
  deferTask,
} from '@jobseeker/common-core/utils';
import {locations} from '@jobseeker/lotus/components/JobSearchBar/data';

export interface JobVisibilityData {
  jobId: string;
  refCode: string;
  jobPos: number;
}

const VISIBILITY_THRESHOLD = 0.2;

const AUDIT_SETTLE_DELAY_MS = 3000;

const SIMILAR_JOBS_PAGE = 'Similar Jobs Page';

const getPageJobId = (pageName: string): string => {
  if (typeof window === 'undefined') return '';
  if (pageName === SIMILAR_JOBS_PAGE) {
    return new URLSearchParams(window.location.search).get('jobId') || '';
  }
  if (pageName === 'Job Description Page') {
    const pathMatch = window.location.pathname.match(/-(\d+)$/);
    if (pathMatch && pathMatch[1]) return pathMatch[1];
  }
  return '';
};

const getSessionUsername = (): string => {
  try {
    return sessionStorage.getItem('userId') || '';
  } catch (e) {
    return '';
  }
};

export interface UseJobVisibilityTrackerOptions {
  batchSize?: number;
  categoryID?: string;
  categoryName?: string;
  tagID?: string;
  tagName?: string;
  filterExp?: string;
  filterLocation?: string;
}

export const useJobVisibilityTracker = (
  options: UseJobVisibilityTrackerOptions = {}
) => {
  const {
    batchSize = 5,
    categoryID,
    categoryName,
    tagID,
    tagName,
    filterExp: filterExpProp,
    filterLocation: filterLocationProp,
  } = options;

  // Set to track already-viewed job IDs (prevents duplicates)
  const viewedJobIdsRef = useRef<Set<string>>(new Set());
  // Array to collect current batch of job data (jobId + refCode)
  const currentBatchRef = useRef<JobVisibilityData[]>([]);
  // Map of element -> job data for observer callback lookup
  const elementToJobDataRef = useRef<Map<Element, JobVisibilityData>>(
    new Map()
  );
  // Single IntersectionObserver instance
  const observerRef = useRef<IntersectionObserver | null>(null);
  // Settle timer for flushing the partial (not-yet-full) batch
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  const isSimilarJobsPageRef = useRef<boolean | null>(null);
  if (isSimilarJobsPageRef.current === null && typeof window !== 'undefined') {
    isSimilarJobsPageRef.current = getUbaPageName() === SIMILAR_JOBS_PAGE;
  }

  const statsRef = useRef({
    mountedAt: 0,
    observerCreated: 0,
    observerReadyAtFirstRef: null as boolean | null,
    registered: 0,
    callbackRuns: 0,
    entriesDelivered: 0,
    intersectingEntries: 0,
    entriesBelowThreshold: 0,
    entriesAtThreshold: 0,
    maxIntersectionRatio: 0,
    eventsFired: 0,
    jobsReported: 0,
    resets: 0,
  });
  const lastAuditSignatureRef = useRef('');

  // Helper function to convert location labels to IDs
  const getLocationIds = (locParam: string): string => {
    if (!locParam) return '';
    const locationLabels = locParam.split('_');
    const ids: number[] = [];

    locationLabels.forEach(label => {
      const location = locations.find(
        (loc: {label: string; value: number; selected: number[]}) =>
          loc.label.replace(/[/\s]/g, '-') === label || loc.label === label
      );
      if (location) {
        if (location.selected && location.selected.length > 0) {
          ids.push(...location.selected);
        } else {
          ids.push(location.value);
        }
      }
    });

    // Remove duplicates and return comma-separated
    return Array.from(new Set(ids)).join(',');
  };

  const fireTrackingEvent = useCallback(
    (jobs: JobVisibilityData[], {immediate = false} = {}) => {
      if (jobs.length === 0) return;
      const onSimilarJobsPage = isSimilarJobsPageRef.current === true;
      const pageName = getUbaPageName();
      const pageNo =
        typeof window !== 'undefined'
          ? parseInt(
              new URLSearchParams(window.location.search).get('page') || '1',
              10
            )
          : 1;
      // console.log('##### FIRING TRACKING EVENT #####');
      // console.log('##### Jobs in batch:', jobs);
      // console.log('##### PageName:', pageName, 'PageNo:', pageNo);
      // console.log('##### Total viewed so far:', viewedJobIdsRef.current.size);

      const urlParams =
        typeof window !== 'undefined'
          ? new URLSearchParams(window.location.search)
          : null;

      // Get jobID based on page type
      const jobID = getPageJobId(pageName);

      let keywords = '';
      if (
        pageName.toLowerCase().includes('search') &&
        typeof window !== 'undefined'
      ) {
        // For AI Search, get keyword from query param
        if (pageName.toLowerCase().includes('ai search')) {
          keywords = urlParams?.get('query') || '';
        } else {
          // For regular search, get from pathname
          const pathMatch = window.location.pathname.match(
            /\/search\/([^/]+)-jobs/
          );
          if (pathMatch && pathMatch[1]) {
            keywords = pathMatch[1];
          }
        }
      }

      // Extract filter params from URL
      const locParam = urlParams?.get('loc') || '';
      const filterLocation = getLocationIds(locParam);
      const filterIndustry = urlParams?.get('industry') || '';
      let minExp = urlParams?.get('minexp') || '';
      let maxExp = urlParams?.get('maxexp') || '';

      // Fallback to localStorage if minexp/maxexp not in URL
      if (!minExp && !maxExp && typeof window !== 'undefined') {
        try {
          const isCatOrTagPage =
            pageName.toLowerCase().includes('category') ||
            pageName.toLowerCase().includes('tag');
          const storageKey = isCatOrTagPage ? 'filterCat' : 'filter';
          const storedFilter = localStorage.getItem(storageKey);
          if (storedFilter) {
            const filterData = JSON.parse(storedFilter);
            minExp = filterData.minexp || '';
            maxExp = filterData.maxexp || '';
          }
        } catch (e) {
          // Ignore localStorage errors
        }
      }

      const filterExp = minExp || maxExp ? `${minExp}-${maxExp}` : '';

      const filterPosting = urlParams?.get('posting') || '';

      const payload = {
        viewedJobs: jobs.map(job => JSON.stringify(job)),
        page: pageName,
        pageName: pageName,
        pageNo: pageNo,
        ...(jobID && (onSimilarJobsPage ? {jobID, jobId: jobID} : {jobID})),
        ...(keywords && {keywords}),
        ...(categoryID && {categoryID}),
        ...(categoryName && {categoryName}),
        ...(tagID && {tagID}),
        ...(tagName && {tagName}),
        ...(filterExpProp || filterExp
          ? {filterExp: filterExpProp || filterExp}
          : {}),
        ...(filterIndustry && {filterIndustry}),
        ...(filterLocationProp || filterLocation
          ? {filterLocation: filterLocationProp || filterLocation}
          : {}),
        ...(filterPosting && {filterPosting}),
      };

      statsRef.current.eventsFired += 1;
      statsRef.current.jobsReported += jobs.length;

      if (onSimilarJobsPage && immediate) {
        TrackEventUBA('jobTuppleView', 'view', payload, {immediate: true});
        return;
      }

      deferTask(() => {
        TrackEventUBA('jobTuppleView', 'view', payload);
      });
    },
    [
      categoryID,
      categoryName,
      tagID,
      tagName,
      filterExpProp,
      filterLocationProp,
    ]
  );

  const flush = useCallback(
    ({immediate = false} = {}) => {
      if (currentBatchRef.current.length > 0) {
        fireTrackingEvent([...currentBatchRef.current], {immediate});
        currentBatchRef.current = [];
      }
    },
    [fireTrackingEvent]
  );

  const flushRef = useRef(flush);
  flushRef.current = flush;

  const clearDebounceTimer = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
  }, []);

  const sendAudit = useCallback((phase: string, reason: string) => {
    if (isSimilarJobsPageRef.current !== true) return;

    const stats = statsRef.current;
    const observerReadyAtFirstRef =
      stats.observerReadyAtFirstRef === null
        ? 'na'
        : stats.observerReadyAtFirstRef
        ? 'yes'
        : 'no';

    const counters = {
      observerCreated: stats.observerCreated,
      observerReadyAtFirstRef,
      registered: stats.registered,
      trackedElements: elementToJobDataRef.current.size,
      callbackRuns: stats.callbackRuns,
      entriesDelivered: stats.entriesDelivered,
      intersectingEntries: stats.intersectingEntries,
      entriesBelowThreshold: stats.entriesBelowThreshold,
      entriesAtThreshold: stats.entriesAtThreshold,
      maxIntersectionRatio: Math.round(stats.maxIntersectionRatio * 100) / 100,
      viewed: viewedJobIdsRef.current.size,
      eventsFired: stats.eventsFired,
      jobsReported: stats.jobsReported,
      pendingAtEnd: currentBatchRef.current.length,
      resets: stats.resets,
    };

    const signature = JSON.stringify(counters);
    if (signature === lastAuditSignatureRef.current) return;
    lastAuditSignatureRef.current = signature;

    const pageName = getUbaPageName();
    const jobID = getPageJobId(pageName);

    TrackUbaAudit('jobTuppleTrackerAudit', {
      phase,
      reason,
      page: pageName,
      pageName: pageName,
      ...(jobID && {jobID, jobId: jobID}),
      msSinceMount: stats.mountedAt ? Date.now() - stats.mountedAt : -1,
      ...counters,
      sessionUsername: getSessionUsername(),
    });
  }, []);

  /**
   * Reset tracking state - flushes current batch and clears viewed set
   * Call this when filters change to start fresh tracking
   */
  const reset = useCallback(() => {
    // console.log('##### RESETTING TRACKER - Filter change #####');
    statsRef.current.resets += 1;
    // First flush any remaining jobs
    flush();
    // Clear the viewed set
    viewedJobIdsRef.current.clear();

    if (isSimilarJobsPageRef.current !== true) {
      elementToJobDataRef.current.clear();
      clearDebounceTimer();
      return;
    }

    clearDebounceTimer();
    const observer = observerRef.current;
    if (!observer) return;
    elementToJobDataRef.current.forEach((_jobData, element) => {
      observer.unobserve(element);
      observer.observe(element);
    });
  }, [clearDebounceTimer, flush]);

  const handleIntersection = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      statsRef.current.callbackRuns += 1;
      statsRef.current.entriesDelivered += entries.length;
      entries.forEach(entry => {
        const ratio = entry.intersectionRatio || 0;
        if (ratio > statsRef.current.maxIntersectionRatio) {
          statsRef.current.maxIntersectionRatio = ratio;
        }

        if (!entry.isIntersecting) return;
        statsRef.current.intersectingEntries += 1;
        if (ratio >= VISIBILITY_THRESHOLD) {
          statsRef.current.entriesAtThreshold += 1;
        } else {
          statsRef.current.entriesBelowThreshold += 1;
        }
        const jobData = elementToJobDataRef.current.get(entry.target);
        if (!jobData) return;
        // Skip if already viewed
        if (viewedJobIdsRef.current.has(jobData.jobId)) return;
        // Mark as viewed
        viewedJobIdsRef.current.add(jobData.jobId);
        // Add to current batch
        currentBatchRef.current.push(jobData);
        // console.log('##### JOB VIEWED #####');
        // console.log('##### Job Data:', jobData);
        // console.log('##### Current batch:', [...currentBatchRef.current]);
        // console.log('##### Viewed set:', Array.from(viewedJobIdsRef.current));
        /*console.log(
          '##### Batch size:',
          currentBatchRef.current.length,
          '/',
          batchSize
        );*/

        // Clear existing debounce timer
        clearDebounceTimer();

        if (currentBatchRef.current.length >= batchSize) {
          // Batch is full - fire immediately. This is what keeps event count
          // bounded: 50 jobs viewed => ~10 events (batches of batchSize), not 50.
          fireTrackingEvent([...currentBatchRef.current]);
          currentBatchRef.current = [];
        } else {
          // Batch not yet full (e.g. only 3-4 cards in the first fold, or the
          // trailing remainder after slow scrolling). Fire the partial batch
          // after a short settle delay so all cards that appeared together are
          // grouped into one event. The delay only ever fires the remainder -
          // it does not cause per-card events, since full batches fire above.
          debounceTimerRef.current = setTimeout(() => {
            if (currentBatchRef.current.length > 0) {
              // console.log('##### SETTLE FLUSH #####');
              flush();
            }
          }, 500);
        }
      });
    },
    [batchSize, clearDebounceTimer, fireTrackingEvent, flush]
  );

  const handlerRef = useRef(handleIntersection);
  handlerRef.current = handleIntersection;

  const ensureObserver = useCallback(() => {
    if (observerRef.current) return observerRef.current;
    if (
      typeof window === 'undefined' ||
      typeof IntersectionObserver === 'undefined'
    ) {
      return null;
    }
    observerRef.current = new IntersectionObserver(
      entries => handlerRef.current(entries),
      {
        root: null, // viewport
        rootMargin: '0px',
        threshold: VISIBILITY_THRESHOLD,
      }
    );
    statsRef.current.observerCreated += 1;
    return observerRef.current;
  }, []);

  useEffect(() => {
    if (isSimilarJobsPageRef.current === true) return;

    // Only create observer on client side
    if (
      typeof window === 'undefined' ||
      typeof IntersectionObserver === 'undefined'
    ) {
      return;
    }

    observerRef.current = new IntersectionObserver(handleIntersection, {
      root: null, // viewport
      rootMargin: '0px',
      threshold: VISIBILITY_THRESHOLD,
    });

    return () => {
      // Clear debounce timer
      clearDebounceTimer();
      // Flush remaining on unmount
      flush();
      // Cleanup observer
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
      // Clear maps
      elementToJobDataRef.current.clear();
    };
  }, [handleIntersection, flush, clearDebounceTimer]);

  useEffect(() => {
    if (isSimilarJobsPageRef.current !== true) return;

    statsRef.current.mountedAt = Date.now();
    ensureObserver();

    const settledAuditTimer = setTimeout(
      () => sendAudit('settled', 'timer'),
      AUDIT_SETTLE_DELAY_MS
    );

    const flushOnHide = (reason: string) => {
      clearDebounceTimer();
      flushRef.current({immediate: true});
      sendAudit('teardown', reason);
    };
    const handlePageHide = () => flushOnHide('pagehide');
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') flushOnHide('hidden');
    };

    window.addEventListener('pagehide', handlePageHide);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('pagehide', handlePageHide);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearTimeout(settledAuditTimer);
      clearDebounceTimer();
      flushRef.current({immediate: true});
      sendAudit('teardown', 'unmount');
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
      elementToJobDataRef.current.clear();
    };
  }, [clearDebounceTimer, ensureObserver, sendAudit]);

  /**
   * Register a job element for visibility tracking
   * Use as ref callback: ref={(el) => registerJobElement(el, jobId, refCode, jobPos)}
   */
  const registerJobElement = useCallback(
    (
      element: Element | null,
      jobId: string,
      refCode: string = '',
      jobPos: number = 0
    ) => {
      if (isSimilarJobsPageRef.current === true) {
        if (!element) return;

        if (statsRef.current.observerReadyAtFirstRef === null) {
          statsRef.current.observerReadyAtFirstRef =
            observerRef.current !== null;
        }

        const observer = ensureObserver();
        if (!observer) return;

        if (viewedJobIdsRef.current.has(jobId)) return;

        elementToJobDataRef.current.set(element, {jobId, refCode, jobPos});
        observer.observe(element);
        statsRef.current.registered += 1;
        return;
      }

      if (!observerRef.current) return;

      // Skip if already viewed
      if (viewedJobIdsRef.current.has(jobId)) return;

      if (element) {
        // Register new element with job data
        elementToJobDataRef.current.set(element, {jobId, refCode, jobPos});
        observerRef.current.observe(element);
      }
    },
    [ensureObserver]
  );

  /**
   * Unregister a job element (call when component unmounts or element changes)
   */
  const unregisterJobElement = useCallback((element: Element | null) => {
    if (!element || !observerRef.current) return;

    observerRef.current.unobserve(element);
    elementToJobDataRef.current.delete(element);
  }, []);

  return {
    /** Register an element for visibility tracking */
    registerJobElement,
    /** Unregister an element from tracking */
    unregisterJobElement,
    /** Force fire tracking for remaining jobs in batch */
    flush,
    /** Reset tracking - flushes batch and clears viewed set (call on filter changes) */
    reset,
    /** Get count of jobs currently in batch (for debugging) */
    getCurrentBatchSize: () => currentBatchRef.current.length,
    /** Get count of total viewed jobs (for debugging) */
    getTotalViewedCount: () => viewedJobIdsRef.current.size,
  };
};

export default useJobVisibilityTracker;
